<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import { copyFilteredCodes, MAX_COPY_ROWS } from '@/shared/helpers/api';
    import { getReleaseLink } from '@/shared/helpers/routing';
    import { http } from '@/shared/httpWrapper';
    import useBuefy from '@/shared/composables/useBuefy';
    import useShow from '@/shared/composables/useShow';
    import CodeSetSelectorModal from '@/shared/components/codesetselector/CodeSetSelectorModal.vue';

    const props = defineProps<{
        codeSetName: string;
        libraryName: string;
        filteredCodeCount: number;
        codeFilter: { regEx: string[]; queries: string[][] };
        disabled?: boolean;
    }>();

    const buefy = useBuefy();
    const { showError } = useShow();
    const router = useRouter();
    const showSelector = ref<boolean>(false);

    function plural(count: number, singular: string, pluralForm: string): string {
        return `${count.toLocaleString()} ${count === 1 ? singular : pluralForm}`;
    }

    const codeCountText = computed(() => plural(props.filteredCodeCount, 'code', 'codes'));

    const tooManyCodes = computed(() => props.filteredCodeCount > MAX_COPY_ROWS);

    const buttonTooltip = computed(() => tooManyCodes.value
        ? `The list shows ${codeCountText.value}, but at most ${MAX_COPY_ROWS.toLocaleString()} can be copied at once. Filter the list to fewer codes.`
        : `Copy the ${codeCountText.value} shown to other scopes`);

    const maxSelected = computed(() => Math.floor(MAX_COPY_ROWS / Math.max(1, props.filteredCodeCount)));

    const limitMessage = computed(() =>
        `With ${codeCountText.value} shown, you can copy to at most ${plural(maxSelected.value, 'library set', 'library sets')} at a time `
        + `(at most ${MAX_COPY_ROWS.toLocaleString()} codes in total). Select fewer library sets, or filter the list to fewer codes.`);

    function onTargetsSelected(targetCodeSets: string[]) {
        showSelector.value = false;
        // Capture the count with the filter, so the dialog states exactly what the request sends
        const codeCount = props.filteredCodeCount;
        const filter = { regEx: [...props.codeFilter.regEx], queries: props.codeFilter.queries.map(q => [...q]) };
        buefy.dialog.confirm({
            title: 'Copy codes to other scopes',
            message: `This will copy ${plural(codeCount, 'code', 'codes')} \u00d7 ${plural(targetCodeSets.length, 'library set', 'library sets')}. `
                + 'Codes that already exist in a target library set will be updated. A release will be used. Any active subscriptions will trigger code publication. Continue?',
            type: 'is-warning',
            hasIcon: true,
            onConfirm: async () => {
                try {
                    const response = await copyFilteredCodes(http, props.codeSetName, { targetCodeSets, ...filter });
                    await router.push(getReleaseLink(response.data.id));
                } catch (error) {
                    showError(error);
                }
            }
        });
    }
</script>

<template>
    <b-tooltip
        :label="buttonTooltip"
        position="is-bottom"
        multilined>
        <b-button
            v-require-can-edit-code="{ libraryName }"
            icon-left="content-duplicate"
            :disabled="disabled || filteredCodeCount === 0 || tooManyCodes"
            @click="showSelector = true">
            Copy to other scopes
        </b-button>
    </b-tooltip>
    <code-set-selector-modal
        v-if="showSelector"
        :library-name="libraryName"
        :source-code-set-name="codeSetName"
        :title="`Copy ${codeCountText} to other scopes`"
        subtitle="The codes currently shown in the list will be copied. Scopes that share a library set are selected together."
        confirm-label="Copy"
        :max-selected="maxSelected"
        :limit-message="limitMessage"
        @confirm="onTargetsSelected"
        @cancel="showSelector = false" />
</template>
